/**
 * DOCUMENTATION:
 * Component: UiUxFundamentals
 * Purpose: Educational page detailing the distinction between UI and UX, and core design philosophies.
 * * * Note for Junior Developers:
 * - A beautiful application that users can't figure out how to use is a failure.
 * - UI (User Interface) is the saddle, the stirrups, and the reins.
 * - UX (User Experience) is the feeling of riding the horse.
 * - Your goal as a Frontend Engineer is to bridge the gap between aesthetics (UI) and usability (UX).
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function UiUxFundamentals() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Philosophies Table
    const corePhilosophies = useMemo(() => [
        { concept: "UI (User Interface)", philosophy: "Visual Communication", description: "The aesthetics. Focuses on typography, color palettes, spacing, and brand identity. It's what the user *sees*." },
        { concept: "UX (User Experience)", philosophy: "Frictionless Journeys", description: "The logic. Focuses on user flows, wireframing, accessibility, and problem-solving. It's how the user *feels*." },
        { concept: "Affordance", philosophy: "Intuitive Interaction", description: "Visual clues in the design that tell the user how an element functions (e.g., a button looking 'clickable' because of a shadow)." },
        { concept: "Cognitive Load", philosophy: "Don't Make Me Think", description: "The amount of mental effort required to use the site. High cognitive load (clutter, unclear labels) causes users to abandon the app." },
    ], []);

    // Coding examples demonstrating how UX translates to code
    const codingModules = useMemo(() => [
        {
            title: "1. Affordance & Visual Feedback",
            code: `/* ❌ BAD UI/UX: The user doesn't know if their click worked */\n.btn-bad {\n  background: blue;\n  color: white;\n}\n\n/* ✅ GOOD UI/UX: Clear state changes communicate with the user */\n.btn-good {\n  background: goldenrod;\n  color: #100720;\n  transition: all 0.2s ease;\n  cursor: pointer; /* Affordance: Tells the user it's clickable */\n}\n\n.btn-good:hover {\n  filter: brightness(1.1); /* Feedback: Mouse is over it */\n}\n\n.btn-good:active {\n  transform: scale(0.95); /* Feedback: The click registered! */\n}`,
            desc: "UX isn't just theory; it's written in CSS. If a button doesn't respond instantly to being hovered or clicked, the user will assume the app is broken or frozen."
        },
        {
            title: "2. Reducing Cognitive Load in Forms",
            code: `\n<input type="text" placeholder="Password">\n\n\n<div class="form-group">\n  <label for="new-password">Create Password <span aria-hidden="true">*</span></label>\n  <input \n    type="password" \n    id="new-password" \n    required \n    aria-describedby="pwd-hint"\n  >\n  <small id="pwd-hint" class="helper-text">\n    Must be at least 8 characters long.\n  </small>\n</div>`,
            desc: "Never rely solely on placeholders; they disappear when the user starts typing, forcing them to rely on short-term memory (High Cognitive Load). Always use persistent labels and helper text."
        },
        {
            title: "3. Defensive Design (Anticipating Errors)",
            code: `// ❌ BAD UX: Fails silently or crashes the app\nasync function fetchUserData() {\n  const response = await fetch('/api/user');\n  const data = await response.json();\n  renderUser(data);\n}\n\n// ✅ GOOD UX: Handles errors gracefully and informs the user\nasync function fetchUserData() {\n  try {\n    showLoadingSpinner();\n    const response = await fetch('/api/user');\n    if (!response.ok) throw new Error('Network response was not ok');\n    const data = await response.json();\n    renderUser(data);\n  } catch (error) {\n    showToastNotification("Oops! We couldn't load your profile. Please try again.");\n  } finally {\n    hideLoadingSpinner();\n  }\n}`,
            desc: "Good UX anticipates that things will go wrong. Network requests fail. Servers crash. A good frontend engineer writes 'defensive code' that catches these errors and shows the user a polite, helpful message instead of a blank white screen."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    UI VS UX: CORE PHILOSOPHIES
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Engineering with Empathy
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Steve Jobs famously said, <em>"Design is not just what it looks like and feels like. Design is how it works."</em> As a frontend developer, you are the last line of defense for the user experience. You can receive a beautiful UI mockup from the design team, but if you code it in a way that is slow, unresponsive, or inaccessible, the UX will be ruined. Understanding the difference between how an app looks (UI) and how it behaves (UX) is what separates a junior coder from a senior product engineer.
                        </Typography>

                        {/* ✅ Core Philosophies Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Essential Terminology</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Concept</div>
                                    <div>Design Focus</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {corePhilosophies.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.concept}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.philosophy}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/uiux-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
